// components/MascotCharacter.jsx
import React, { useState, useEffect, useRef } from 'react';

/**
 * Interactive Mascot Character Component
 * Renders an animated character that jumps to products/elements on the kiosk
 * 
 * Props:
 * - mediaItem: Media object with metadata configuration
 * - apiBase: API base URL for loading mascot file
 */
export default function MascotCharacter({ mediaItem, apiBase }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isJumping, setIsJumping] = useState(false);
  const mascotRef = useRef(null);
  const intervalRef = useRef(null);

  const config = mediaItem.metadata || {};
  const jumpInterval = config.jump_interval || 5000;
  const targetElements = config.target_elements || 'products';
  const animationType = config.animation_type || 'gif';
  const mediaUrl = `${apiBase}/uploads/${mediaItem.file_path}`;

  useEffect(() => {
    // Start jumping behavior
    const startJumping = () => {
      intervalRef.current = setInterval(() => {
        jumpToRandomTarget();
      }, jumpInterval);
    };

    // Initial random position
    setPosition({
      x: Math.random() * (window.innerWidth - 100),
      y: Math.random() * (window.innerHeight - 100)
    });

    startJumping();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [jumpInterval]);

  function jumpToRandomTarget() {
    let targets = [];

    // Find target elements based on configuration
    if (targetElements === 'products') {
      // Find product cards or items
      targets = document.querySelectorAll('.product-card, .catalog-item, [data-product]');
      console.log('[Mascot] Found product targets:', targets.length);
    } else if (targetElements === 'categories') {
      // Find category elements
      targets = document.querySelectorAll('.category-card, [data-category]');
      console.log('[Mascot] Found category targets:', targets.length);
    } else {
      // Random movement
      const newX = Math.random() * (window.innerWidth - 100);
      const newY = Math.random() * (window.innerHeight - 100);
      animateJump(newX, newY);
      return;
    }

    if (targets.length === 0) {
      // Fallback to random movement if no targets found
      console.log('[Mascot] No targets found, using random movement');
      const newX = Math.random() * (window.innerWidth - 100);
      const newY = Math.random() * (window.innerHeight - 100);
      animateJump(newX, newY);
      return;
    }

    // Pick random target
    const randomTarget = targets[Math.floor(Math.random() * targets.length)];
    const rect = randomTarget.getBoundingClientRect();
    
    console.log('[Mascot] Jumping to target:', {
      element: randomTarget,
      position: { left: rect.left, top: rect.top, width: rect.width }
    });
    
    // Jump to target position (centered above it)
    const newX = rect.left + rect.width / 2 - 40; // 40 = half mascot width
    const newY = rect.top - 100; // Above the target

    animateJump(newX, newY);
  }

  function animateJump(toX, toY) {
    setIsJumping(true);
    setPosition({ x: toX, y: toY });
    
    // End jump animation after 1 second
    setTimeout(() => {
      setIsJumping(false);
    }, 1000);
  }

  return (
    <div
      ref={mascotRef}
      className={`mascot-character ${isJumping ? 'jumping' : 'idle'}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
    >
      {animationType === 'gif' ? (
        <img src={mediaUrl} alt="Mascot" className="mascot-image" />
      ) : animationType === 'lottie' ? (
        <div className="mascot-lottie">
          {/* Lottie player would go here - requires lottie-web library */}
          <img src={mediaUrl} alt="Mascot" className="mascot-image" />
        </div>
      ) : (
        <div className="mascot-placeholder">🦸‍♂️</div>
      )}

      <style jsx="true">{`
        .mascot-character {
          position: fixed;
          width: 80px;
          height: 80px;
          z-index: 9999;
          pointer-events: none;
          transition: left 1s cubic-bezier(0.34, 1.56, 0.64, 1), 
                      top 1s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .mascot-character.jumping {
          animation: bounce 1s ease-in-out;
        }

        .mascot-character.idle {
          animation: float 3s ease-in-out infinite;
        }

        .mascot-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .mascot-placeholder {
          font-size: 64px;
          text-align: center;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-40px) scale(1.1); }
          50% { transform: translateY(-60px) scale(1.15) rotate(10deg); }
          75% { transform: translateY(-40px) scale(1.1) rotate(-10deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}
